import { ApiError, unwrap } from "@brad-tipper/app-frontend";

import { API_URL } from "~/lib/constants";
import { api, sessionStore } from "./client";

export type UploadPurpose = "receipt" | "attachment" | "logo";

export type UploadedFile = {
  path: string;
  size_bytes: number;
  mime_type: string;
};

/** Stores a file for a workspace; the server names it and returns its path. */
export async function uploadFile(input: {
  workspaceId: string;
  purpose: UploadPurpose;
  body: ArrayBuffer;
  mimeType: string;
}): Promise<UploadedFile> {
  const token = await sessionStore.getAccessToken();
  const query = new URLSearchParams({
    workspace_id: input.workspaceId,
    purpose: input.purpose,
  });
  let response: Response;
  try {
    response = await fetch(`${API_URL}/v1/uploads?${query.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": input.mimeType,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: input.body,
    });
  } catch {
    throw new ApiError({
      code: "network_error",
      detail: "Unable to reach the server. Check your connection.",
    });
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      (payload as ConstructorParameters<typeof ApiError>[0] | null) ?? {
        detail: "Upload failed.",
      },
      response.status,
    );
  }
  return payload as UploadedFile;
}

/** A short-lived URL for a stored private file. */
export async function signedFileUrl(path: string): Promise<string> {
  const { url } = await unwrap(api.getUploadUrl({ query: { path } }));
  return url;
}

/** Best-effort delete of a stored file. */
export async function removeFile(path: string | null | undefined) {
  if (!path) return;
  try {
    await unwrap(api.deleteUpload({ query: { path } }));
  } catch {
    // An orphaned file is harmless; failing the save over it is not.
  }
}
